import React, { useRef, useCallback, useMemo } from 'react'
import { useDragDropContext } from './DragDropContext'
import { DragItem } from './useCustomDragDrop'
import { noop } from 'lodash-es'

export function Draggable({
  id,
  index,
  droppableId,
  children,
  enabled,
}: {
  id: string
  index: number
  droppableId: string
  children: (props: {
    isDragging: boolean
    dragHandleProps: {
      onMouseDown: (e: React.MouseEvent) => void
      onTouchStart: (e: React.TouchEvent) => void
    }
  }) => React.ReactNode
  enabled: boolean
}) {
  const elementRef = useRef<HTMLDivElement>(null)
  const { dragState, startDrag } = useDragDropContext()

  const item = useMemo<DragItem>(() => ({ id, index, droppableId }), [id, index, droppableId])
  const isDragging = dragState.isDragging && dragState.draggedItem?.id === id

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (elementRef.current) {
        startDrag(e, item, elementRef.current)
      }
    },
    [startDrag, item]
  )

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (elementRef.current) {
        startDrag(e, item, elementRef.current)
      }
    },
    [startDrag, item]
  )

  return (
    <div
      ref={elementRef}
      data-draggable-id={id}
      data-draggable-index={index}
      style={{
        userSelect: 'none',
        pointerEvents: enabled ? 'auto' : 'none',
      }}
    >
      {children({
        isDragging,
        dragHandleProps: enabled
          ? {
              onMouseDown: handleMouseDown,
              onTouchStart: handleTouchStart,
            }
          : {
              onMouseDown: noop,
              onTouchStart: noop,
            },
      })}
    </div>
  )
}
